/* ══════════════════════════════════════════════════════════════════════════
   sysart.js — one moving picture per failure.

   These loop, and the loop is the point. A failure screen is read at a glance
   by someone who is already annoyed, and a still picture makes them read the
   sentence to find out what kind of trouble they are in. A shape that keeps
   failing the same way says it before the sentence does: 429 fills and refuses
   to fill further, 500 draws a break and then takes it back, 501 draws itself
   forever and never fills in.

   Nothing here names anything behind the console. The picture is of the
   operator's situation, not of the machine's.
   ══════════════════════════════════════════════════════════════════════════ */
import { html, M, F, Ico } from "./ui.js";
import { Rings, Sigil } from "./loading.js";

const EASE  = [.16,1,.3,1];
const LOOP  = (d, extra={}) => ({ duration:d, repeat:Infinity, ease:"easeInOut", ...extra });
const MONO  = { fontFamily:"var(--mono)", fontSize:"var(--t-micro)", color:"var(--fg-3)",
                letterSpacing:".12em", textTransform:"uppercase" };
const Cap = ({ children, tone }) => html`
  <div style=${{ ...MONO, color:tone||MONO.color }}>${children}</div>`;

export function Art({ kind, code }){
  switch(kind){

    /* ─── 400 · the shape that never closes ────────────────────────────────
       Three sides land. The fourth is always drawn past its corner, so the
       square never becomes a square — which is the whole of what a malformed
       request is. */
    case "malform": return html`
      <div className="art art--col">
        <svg viewBox="0 0 132 132" width="146" height="146" fill="none">
          ${[["M26 26 H106", 0],["M106 26 V106", .28],["M106 106 H26", .56]].map(([d,delay])=> html`
            <${M.path} key=${d} d=${d} stroke="rgba(233,239,238,.34)" strokeWidth="1.6" strokeLinecap="round"
              initial=${{ pathLength:0 }}
              animate=${{ pathLength:[0,1,1,0] }}
              transition=${{ duration:3.6, times:[0,.22,.78,1], delay, repeat:Infinity, ease:EASE }}/>`)}
          ${/* the side that overshoots its corner, every single time */""}
          <${M.path} d="M26 106 V18" stroke="var(--amber)" strokeWidth="1.8" strokeLinecap="round"
            initial=${{ pathLength:0 }}
            animate=${{ pathLength:[0,1,1,0] }}
            transition=${{ duration:3.6, times:[0,.3,.78,1], delay:.84, repeat:Infinity, ease:EASE }}/>
          <${M.g} animate=${{ opacity:[0,0,1,1,0] }}
            transition=${{ duration:3.6, times:[0,.34,.42,.78,1], repeat:Infinity, ease:"easeOut" }}>
            <path d="M20 24 L32 12 M20 12 L32 24" stroke="var(--coral)" strokeWidth="1.6" strokeLinecap="round"/>
          <//>
        </svg>
        <${Cap}>refused on sight<//>
      </div>`;

    /* ─── 401 · the cut that travels ──────────────────────────────────────
       You are not shut out at one end; the session is severed along its
       length and both ends know it. Then it heals, because signing in again
       is all this costs. */
    case "sever": return html`
      <div className="art art--col">
        <svg viewBox="0 0 200 60" width="212" height="64" fill="none">
          <${M.path} d="M22 30 H100" stroke="var(--aqua)" strokeWidth="1.7" strokeLinecap="round"
            animate=${{ x:[0,-14,-14,0] }} transition=${LOOP(3.8,{ times:[0,.42,.8,1] })}/>
          <${M.path} d="M100 30 H178" stroke="rgba(233,239,238,.3)" strokeWidth="1.7" strokeLinecap="round"
            animate=${{ x:[0,14,14,0] }} transition=${LOOP(3.8,{ times:[0,.42,.8,1] })}/>
          <circle cx="22" cy="30" r="5.5" fill="var(--aqua)"/>
          <${M.circle} cx="178" cy="30" r="5.5"
            animate=${{ fill:["#3DE1C8","#3DE1C8","#FF6B6B","#FF6B6B","#3DE1C8"] }}
            transition=${LOOP(3.8,{ times:[0,.3,.44,.8,1] })}/>
          ${/* the cut itself, sweeping the line before it parts */""}
          <${M.rect} y="18" width="2" height="24" rx="1" fill="var(--coral)"
            animate=${{ x:[24,176,176,24], opacity:[0,1,0,0] }}
            transition=${LOOP(3.8,{ times:[0,.3,.46,1], ease:"easeIn" })}/>
        </svg>
        <${Cap}>every device, at once<//>
      </div>`;

    /* ─── 403 · the gate that holds ───────────────────────────────────────
       Something arrives, the bars answer it, and it goes back where it came
       from. It is not damage — nothing about it is red until it insists. */
    case "gate": return html`
      <div className="art art--col">
        <div className="gateart">
          ${[40,64,88,106,88,64,40].map((h,i)=> html`
            <${M.i} key=${i} style=${{ height:h }}
              animate=${{ scaleY:[1,1,.62,1], opacity:[.55,.55,1,.55] }}
              transition=${LOOP(3.4,{ times:[0,.34,.5,1], delay:i*.045 })}/>`)}
          <${M.s} className="gateart__tok"
            animate=${{ x:[-92,-4,-92,-92], opacity:[0,1,1,0] }}
            transition=${LOOP(3.4,{ times:[0,.36,.62,1] })}/>
        </div>
        <div style=${{ display:"flex", alignItems:"center", gap:8, color:"var(--amber)" }}>
          <${Ico} d="lock" w=${13}/><${Cap} tone="var(--amber)">held at your role<//>
        </div>
      </div>`;

    /* ─── 404 · the sweep that finds nothing ──────────────────────────────
       A search that keeps running and keeps coming back empty. The one blip
       it catches is gone before the arm comes round again. */
    case "sweep": return html`
      <div className="art">
        <div className="sweep">
          <i/><i/><i/>
          <${M.s} className="sweep__arm"
            animate=${{ rotate:360 }} transition=${{ duration:3.4, repeat:Infinity, ease:"linear" }}/>
          <${M.u} className="sweep__blip"
            animate=${{ opacity:[0,.9,0,0] }} transition=${LOOP(3.4,{ times:[0,.06,.3,1] })}/>
          <b className="sweep__n">404</b>
        </div>
      </div>`;

    /* ─── 429 · the brake ─────────────────────────────────────────────────
       Each tier fills faster than the last one drains. The third never gets
       its turn while you are watching, which is the warning. */
    case "meter": return html`
      <div className="art art--col">
        <div className="meterart">
          ${[["first","60 s",1.6,0],["second","120 s",2.6,.5],["third","300 s",4.4,1]].map(([t,d,dur,delay],i)=> html`
            <${F} key=${t}>
              <div className="bar">
                <${M.i} style=${{ transformOrigin:"left", background: i===2 ? "rgba(255,255,255,.16)" : "var(--amber)" }}
                  animate=${{ scaleX:[0, i===2 ? .06 : 1, i===2 ? .06 : 1, 0] }}
                  transition=${{ duration:dur+3, times:[0,.38,.76,1], delay, repeat:Infinity, ease:EASE }}/>
              </div>
              <div className="lbl"><span>${t} trip</span><span>${d}</span></div>
            <//>`)}
        </div>
        <${Cap}>it lets go on its own<//>
      </div>`;

    /* ─── 500 · drawn, then taken back ────────────────────────────────────
       The break propagates and then retreats along its own path. That
       retreat is the rollback, and it is the only reassuring thing on the
       screen, so it is the thing that moves. */
    case "fracture": return html`
      <div className="art art--col">
        <svg viewBox="0 0 190 110" width="196" height="114" fill="none">
          <path d="M10 62 H180" stroke="rgba(233,239,238,.12)" strokeWidth="1.2" strokeDasharray="3 4"/>
          <${M.path} d="M10 62 H54 L70 30 L86 92 L102 44 L118 74 L134 62 H180"
            stroke="var(--coral)" strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round"
            initial=${{ pathLength:0 }}
            animate=${{ pathLength:[0,1,1,0,0] }}
            transition=${{ duration:4.4, times:[0,.32,.5,.82,1], repeat:Infinity, ease:EASE }}/>
          <${M.circle} cx="86" cy="92" r="4"
            animate=${{ opacity:[0,1,1,0,0], scale:[.4,1,1,.4,.4] }}
            transition=${{ duration:4.4, times:[0,.34,.5,.8,1], repeat:Infinity, ease:"easeOut" }}
            fill="var(--coral)"/>
        </svg>
        <${Cap}>put back the way it was<//>
      </div>`;

    /* ─── 502 · the pulse that does not arrive ────────────────────────────
       It leaves, it crosses the first span, and it dies in the gap. You are
       watching the reach fail, not the site fail. */
    case "relay": return html`
      <div className="art art--col">
        <div className="relay">
          <u>you</u><s/><u>the door</u><s data-bad="1"/><u data-bad="1">the system</u>
          <${M.b} className="relay__pulse"
            animate=${{ x:[6,92,120,120], opacity:[0,1,0,0], scale:[1,1,.3,.3] }}
            transition=${LOOP(2.8,{ times:[0,.46,.62,1], ease:"easeIn" })}/>
        </div>
        <${Cap}>your site is still running<//>
      </div>`;

    /* ─── 503 · work turned away at the door ──────────────────────────────
       Requests arrive and are deflected aside, one after another. Nothing is
       broken in the picture; it is simply not accepting. */
    case "shed": return html`
      <div className="art art--col">
        <svg viewBox="0 0 190 100" width="196" height="104" fill="none">
          <${M.path} d="M112 14 V86" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round"
            animate=${{ opacity:[.5,1,.5] }} transition=${LOOP(2.2)}/>
          ${[0,1,2].map(i=> html`
            <${M.g} key=${i}
              animate=${{ x:[-104,0,0], y:[0,0, i===1 ? 34 : -34], opacity:[0,1,0] }}
              transition=${{ duration:2.6, times:[0,.52,1], delay:i*.62, repeat:Infinity, ease:"easeOut" }}>
              <rect x="96" y="44" width="13" height="13" rx="3.5"
                stroke="rgba(233,239,238,.5)" strokeWidth="1.3"/>
            <//>`)}
          <path d="M124 50 H178" stroke="rgba(61,225,200,.3)" strokeWidth="1.2" strokeDasharray="3 4"/>
          <text x="152" y="40" textAnchor="middle" fill="rgba(61,225,200,.55)"
            style=${{ fontFamily:"var(--mono)", fontSize:9, letterSpacing:".14em" }}>LOOKING OK</text>
        </svg>
        <${Cap} tone="var(--amber)">changes refused for now<//>
      </div>`;

    /* ─── offline · the line gone slack ───────────────────────────────────*/
    case "tether": return html`
      <div className="art art--col">
        <svg viewBox="0 0 190 64" width="196" height="66" fill="none">
          <${M.path} stroke="rgba(233,239,238,.24)" strokeWidth="1.5" strokeLinecap="round"
            animate=${{ d:["M12 16 Q95 58 178 16","M12 16 Q95 36 178 16","M12 16 Q95 58 178 16"] }}
            transition=${LOOP(4)}/>
          <circle cx="12" cy="16" r="5" fill="var(--aqua)"/>
          <${M.circle} cx="178" cy="16" r="5" fill="rgba(233,239,238,.28)"
            animate=${{ opacity:[.25,.85,.25] }} transition=${LOOP(2.1)}/>
        </svg>
        <div style=${{ display:"flex", alignItems:"center", gap:8, color:"var(--fg-3)" }}>
          <${Ico} d="wifiOff" w=${13}/><${Cap}>frozen at 03:24:02<//>
        </div>
      </div>`;

    /* ─── boot · the same mark every wait in this product uses ────────────*/
    case "boot": return html`
      <div className="art art--col">
        <div className="bootmark"><${Rings} w=${116}/><span><${Sigil} w=${46}/></span></div>
        <${Cap} tone="var(--ai)">coming to readiness<//>
      </div>`;

    default: return html`
      <div className="art"><span className="bigcode" data-tone="bad">${code}</span></div>`;
  }
}
